import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchData } from "../api/client";

/**
 * Lista slots do fluxo de chatbot por usuário (ChatBot) e opcionalmente por pai.
 * @param {number|null} userId - ID do usuário (ura=1)
 * @param {number|null} parentId - id_pai (null = não filtra)
 */
export function useChatbotSlots(userId, parentId = null) {
  return useQuery(
    ["chatbot", "slots", userId, parentId],
    async () => {
      if (!userId) return [];
      const params = new URLSearchParams({ user_id: String(userId) });
      if (parentId !== null && parentId !== undefined) params.set("parent_id", String(parentId));
      const res = await fetchData(`/chatbot/slots?${params.toString()}`, "GET");
      const data = res?.data ?? res ?? [];
      return Array.isArray(data) ? data : [];
    },
    { enabled: !!userId, staleTime: 30000 }
  );
}

/**
 * Lista ações disponíveis para configuração dos slots do ChatBot.
 * As ações dependem do usuário (tipo 2 usa a árvore de slots, tipo 3 as regras de automação).
 */
export function useChatbotActions(userId) {
  return useQuery(
    ["chatbot", "actions", userId],
    async () => {
      if (!userId) return [];
      const params = new URLSearchParams({ user_id: String(userId) });
      const res = await fetchData(`/chatbot/actions?${params.toString()}`, "GET");
      const data = res?.data ?? res ?? [];
      return Array.isArray(data) ? data : [];
    },
    {
      enabled: !!userId,
      staleTime: 30000,
    }
  );
}

/**
 * Obtém um slot por ID.
 */
export function useChatbotSlot(slotId) {
  return useQuery(
    ["chatbot", "slot", slotId],
    async () => {
      const res = await fetchData(`/chatbot/slots/${slotId}`, "GET");
      return res?.data ?? res ?? null;
    },
    { enabled: !!slotId }
  );
}

/**
 * Mutations: criar, atualizar e excluir slot.
 */
export function useChatbotMutations(userId) {
  const queryClient = useQueryClient();

  const createSlot = useMutation(
    async (payload) => {
      const body = { ...payload, user_id: payload.user_id ?? userId };
      const res = await fetchData("/chatbot/slots", "POST", body);
      return res?.data ?? res;
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["chatbot", "slots", variables.user_id ?? userId]);
        queryClient.invalidateQueries(["chatbot"]);
      },
    }
  );

  const updateSlot = useMutation(
    async ({ id, ...payload }) => {
      const res = await fetchData(`/chatbot/slots/${id}`, "PUT", payload);
      return res?.data ?? res;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["chatbot"]);
      },
    }
  );

  const deleteSlot = useMutation(
    async (id) => {
      await fetchData(`/chatbot/slots/${id}`, "DELETE");
      return id;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["chatbot"]);
      },
    }
  );

  return { createSlot, updateSlot, deleteSlot };
}
